import { SocialMediaPostData } from "@contracts/shareStop";
import { PhotoGalleryCustomerData } from "@contracts/photosPageData";

export type { ContentCalendarUserSettingsData } from "@contracts/socialMediaSettings";

/**
 * Summary info for a calendar (used in calendar picker)
 */
export interface CalendarInfo {
  calendarId: string;
  calendarName: string;
  socialChannelCount?: number;
  lastUpdated?: string;
}

export type ContentCalendarInputSource = "local" | "template";

export interface ContentCalendarInput {
  id: string;

  customerId: string;
  title: string;
  description: string;
  date: string;
  source: ContentCalendarInputSource;
  // Optional external URL associated with the calendar input
  url?: string | null;

  order?: number | null;

  // Audit fields
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface GetCalendarInputsResponse {
  inputs: Array<ContentCalendarInput>;
  shouldCreateLocalEvents: boolean;
  shouldGenInterests: boolean;
}

export interface CreateContentCalendarInputRequest {
  customerId: string;
  title: string;
  description: string;
  date: string;
  source: ContentCalendarInputSource;
}

export interface GenerateContentCalendarRequest {
  userGuidance?: string;
  genStartDate?: string;
  matchContent?: boolean;
  saveResults?: boolean;
  deleteAndRegen?: boolean; // Delete the current set and regenerates
}

export interface ContentMatchRequest {
  overwriteIfSet?: boolean; // If true, will overwrite the current contentMatch if it already exists
}

export enum ContentSummaryStatus {
  New = "New",
  Done = "Done",
  Failed = "Failed",
  Redo = "Redo",
}

export interface ContentSummaryData {
  id: string;

  customerId: string;
  contextType: ContentSummaryContextType;
  contextId: string;
  contextName: string;
  excludeFromIndex?: { aiSummary?: string };

  photoCount: number;
  videoCount: number;
  assetCount: number;

  // Example asset IDs from the content group
  examples: Array<string>;

  status: ContentSummaryStatus;

  lastProcessed: string;

  // Audit fields
  createdOn: string;
  createdBy: string;
  updatedOn: string | null;
  updatedBy: string | null;
}

export interface ContentSummaryPreview {
  id: string;
  contextType: ContentSummaryContextType;
  contextId: string;
  status: ContentSummaryStatus;
  photoCount: number;
  videoCount: number;
  assetCount: number;
  contentDate: string | null; // DateOnly as ISO string (optional)
  lastProcessed: string | null;
  examples: Array<string>;
  contextName?: string;
}

export interface ContentSummaryFullPayload {
  content: Array<ContentSummaryData>;
}

export interface ContentSummaryPreviewPayload {
  content: Array<ContentSummaryPreview>;
}

export interface ContentSummaryProcessingItem {
  id: string;
  contextName: string;
  contextType: ContentSummaryContextType;
  contextId: string;
  status: ContentSummaryStatus;
}

export interface ContentSummaryProcessingPayload {
  content: Array<ContentSummaryProcessingItem>;
}

export interface ContentSummaryStatusResults {
  completeCount: number;
  needsProcessingCount: number;
  failedCount: number;
}

export enum ContentSummaryContextType {
  CommunityUpload = "CommunityUpload",
  CustomerUpload = "CustomerUpload",
  CreatorVisit = "CreatorVisit",
  Location = "Location",
}

export enum CalendarItemGenStatus {
  Draft = "Draft",
  Pending = "Pending",
  Generating = "Generating",
  Completed = "Completed",
  Failed = "Failed",
}

export enum CalendarItemCreationSource {
  FullManual = "FullManual",
  UserCreated = "UserCreated",
  WeeklyGen = "WeeklyGen",
  CreatorVisitComplete = "CreatorVisitComplete",
}

export interface GenMetaInfo {
  sourceType?: string;
  locationExternalId?: string;
  contentFit?: string;
  affinityScore?: number;
  candidateContentSummaryIds?: Array<string>;
  rationale?: string;
  foundContent?: boolean;
}

export interface ContentCalendarItemContentMatch {
  contentSummaryId: string;
  contextType: ContentSummaryContextType;
  contextId: string | null;
  contextName: string | null;
  photoCount: number;
  assetCount: number;
  order: number;
}

export interface ContentCalendarItem {
  entityType: "ContentCalendarItem";

  id: string;

  matchRationale: string;
  genStatus: CalendarItemGenStatus;
  genMetaInfo?: GenMetaInfo;

  // Entity-specific properties
  customerId: string;
  title: string;
  description: string | null;
  weekGroup: string; // e.g. "2025-W27"
  specificDate?: string | null;
  order: number;
  contentSourceMatches?: Array<ContentCalendarItemContentMatch> | null;
  creationFlow?: CalendarItemCreationSource | null;

  // Audit fields
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface ContentCalendarItemMatch {
  calendarItem: ContentCalendarItem;
  socialPost?: SocialMediaPostData;
  loadedContentMatches?: Array<ContentSummaryPreview>;
  isLoadingContentMatches?: boolean;
}

export interface ContentCalendarWeekMatch {
  weekGroup: string;
  weekGroupName: string;
  postWillGenDate?: string;
  userGuidance?: string | null;
  systemTopics?: string | null;
  items: Array<ContentCalendarItemMatch>;
}

export interface SaveWeekGuidanceRequest {
  weekKey: string;
  userGuidance: string | null;
}

export interface ContentCalendarGuidanceData {
  id: string;
  customerId: string;
  calendarId: string;
  weekKey: string;
  excludeFromIndex?: { userGuidance?: string | null; systemTopics?: string | null };
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

export interface ContentCalendarPrimaryGroup {
  weeks: Array<ContentCalendarWeekMatch>;
}

export enum CalendarPrimaryGroupName {
  Past = "past",
  Upcoming = "upcoming",
}

export interface CalendarInputInfo {
  contentSummaryCount: number;
  contentSummaryByType: Record<string, number>;
}

export interface ContentCalendarMatchedResults {
  [CalendarPrimaryGroupName.Past]: ContentCalendarPrimaryGroup;
  [CalendarPrimaryGroupName.Upcoming]: ContentCalendarPrimaryGroup;

  currentOperation: SocialOperationType | null;
  nextPostGenForWeek: string;
  nextPostGenOnWeek: string;
  topicFilterCount: number;
  hasPastPosts?: boolean;
  enableCreateManyPosts?: boolean;
  maxCreateManyPosts?: number;
}

export interface StatusChangeCalendarItemInput {
  id: string;
  genStatus: CalendarItemGenStatus;
}

export interface StatusChangeCheckRequest {
  inputs: Array<StatusChangeCalendarItemInput>;
}

export interface SocialContentUploadPayload {
  uploadedForPostTitle: string;
  photoIds: Array<string>;
  clientSideDateTime: string; // DateTimeOffset
}

export interface SocialContentUploadResult {
  contentSummaryData: ContentSummaryPreview;
  photoGallery: PhotoGalleryCustomerData;
}

export interface SocialContentUploadRequirementsResult {
  minimumAssetsForInitialPostGen: number;
}

export enum SocialPostType {
  Post = "Post",
  Story = "Story",
  Reel = "Reel",
}

export interface MatchAndGeneratePostRequest {
  title: string;
  description: string;
  weekGroup?: string | null;
  contentSourceType?: ContentSummaryContextType | null;
  sourceEntityId?: string | null;
  generateManyPosts?: boolean;
}

export interface MatchAndGeneratePostResponse {
  calendarItem: ContentCalendarItem;
  calendarItems: ContentCalendarItem[];
  failedToMatch: boolean;
}

export interface FullManualPostCreationRequest {
  caption: string;
  assetIds: string[];
  postType?: SocialPostType;
  weekGroup?: string | null;
  contentSourceType?: ContentSummaryContextType | null;
  sourceEntityId?: string | null;
  manualUploadsSelected?: number;
  locationTagSettings?: import("@contracts/socialLocations").SocialPostLocationTagSettingsData[];
  physicalLocationId?: string | null;
  instagramCollaboratorHandles?: string[];
}

export interface FullManualPostCreationResponse {
  newItemAndPost: ContentCalendarItemMatch;
}

export interface CalendarDashboardSummaryResult {
  postsPendingApproval: Array<SocialMediaPostData>;
  scheduledNotPublishedCount: number;
  nextPostGenForWeek: string | null;
  nextPostGenOnWeek: string | null;
  nextPostScheduledOn: string | null;
  postsPerWeek: number;
  weeksToGenPosts: number;
}

export enum SocialOperationType {
  NeedInitialContent = "NeedInitialContent",
  GeneratingInitialPosts = "GeneratingInitialPosts",
  LackingContentVariety = "LackingContentVariety",
}

export interface CalendarStatusResult {
  currentOperation: SocialOperationType | null;
}

export interface GenerateDespiteLackingVarietyResult {
  kickedOff: boolean;
}

export interface CalendarGenResultSummaryData {
  id: string;
  customerId: string;
  calendarId: string;
  startDate: string; // DateOnly as ISO string
  weeksGenerated: number;
  contentSummariesCount: number;
  totalPhotoCount: number;
  topicsGenerated: number;
  topicsMatched: number;
  topicSummary: string | null;
  moreContentNeeded: string | null;
  moreContentGuidance: string | null;
  history: string | null;
  moreContentNeededRationale: string | null;
  status: "New" | "Done" | "Failed" | "Redo";
  isProcessing: boolean;
  setId: string;
  source: "Manual" | "InitialSetup" | "WeeklyJob";
  createdDate: string;
  lastRequestedProcessingDate: string | null;
  updatedDate: string;
}

export interface NextFutureCalendarResult {
  nextCalendarGenSummary: CalendarGenResultSummaryData | null;
  longTermCalendarGenSummary: CalendarGenResultSummaryData | null;
}
